import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Swords, Trophy, Users } from "lucide-react";
import { getMyPrivateChallenges } from "utils/supabase";

const PAGE_SIZE = 5;

const PODIUM_COLORS = {
  1: { bg: "#fef9c3", border: "#facc15", color: "#b45309" },
  2: { bg: "#f1f5f9", border: "#cbd5e1", color: "#475569" },
  3: { bg: "#fff7ed", border: "#fdba74", color: "#c2410c" },
};
const DEFAULT_RANK_COLORS = { bg: "#ede9fe", border: "#ddd6fe", color: "#7c3aed" };

function RankBadge({ position }) {
  const colors = PODIUM_COLORS[position] || DEFAULT_RANK_COLORS;

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: "9999px",
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.color,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      <Trophy size={16} />
      {position ? (
        <Typography sx={{ fontSize: 9, fontWeight: 800, lineHeight: 1, mt: 0.2 }}>#{position}</Typography>
      ) : null}
    </Box>
  );
}

export function ChallengeRow({ challenge, onNavigate, isLast }) {
  const isCompleted = String(challenge?.exam_status || "").toLowerCase() === "completed";
  const hasScore = isCompleted && challenge?.score !== null && challenge?.score !== undefined;
  const scoreLabel = hasScore ? `${Math.round(Number(challenge.score))}%` : null;
  const hasPosition = isCompleted && challenge?.position !== null && challenge?.position !== undefined;
  const durationLabel = challenge?.duration_minutes ? `${challenge.duration_minutes} min` : "∞ min";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: 1,
        px: { xs: 1.1, sm: 1.4 },
        py: { xs: 1, sm: 1.2 },
        borderBottom: isLast ? "none" : "1px solid #f1f5f9",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
        <RankBadge position={hasPosition ? challenge.position : null} />

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: "#0f172a",
              fontSize: "0.9rem",
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {challenge?.title || "Reto"}
          </Typography>
          <Typography
            sx={{
              mt: 0.2,
              color: "#64748b",
              fontSize: "0.76rem",
              lineHeight: 1.2,
            }}
          >
            {challenge?.question_count} preguntas · {durationLabel} ·{" "}
            {challenge?.completed_count ?? 0}/{challenge?.participants_count ?? 0} completados
          </Typography>
        </Box>
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.6} flexShrink={0} flexWrap="wrap" useFlexGap>
        {scoreLabel ? (
          <Box
            sx={{
              borderRadius: "8px",
              px: 1,
              py: 0.4,
              textAlign: "center",
              color: "#15803d",
              backgroundColor: "#ecfdf5",
              fontSize: "0.84rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {scoreLabel}
          </Box>
        ) : null}

        <Button
          size="small"
          variant="outlined"
          startIcon={<Trophy size={13} />}
          onClick={() => onNavigate(`/admin/reto/${challenge.invite_code}/resultados`)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            color: "#7c3aed",
            borderColor: "#c4b5fd",
            fontSize: "0.8rem",
            lineHeight: 1.2,
            px: 1.4,
            py: 0.55,
            minWidth: "auto",
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "#a78bfa", backgroundColor: "#f5f3ff" },
          }}
        >
          Leaderboard
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={() =>
            onNavigate(
              isCompleted
                ? `/admin/simulador/${challenge.exam_id}/resultados`
                : challenge?.exam_id
                  ? `/admin/simulador/${challenge.exam_id}`
                  : `/admin/reto/${challenge.invite_code}`
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            fontSize: "0.8rem",
            lineHeight: 1.2,
            px: 1.4,
            py: 0.55,
            minWidth: "auto",
            whiteSpace: "nowrap",
            bgcolor: "#7c3aed",
            boxShadow: "none",
            "&:hover": { bgcolor: "#6d28d9", boxShadow: "none" },
          }}
        >
          {isCompleted ? "Ver resultados" : "Continuar"}
        </Button>
      </Stack>
    </Box>
  );
}

export default function CardMisRetos() {
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [challenges, setChallenges] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function fetchChallenges() {
      setIsLoading(true);
      try {
        const { data, error } = await getMyPrivateChallenges(page, PAGE_SIZE);

        if (!mounted) return;

        if (error || !data?.success) {
          console.error("Error fetching private challenges:", error);
          setChallenges([]);
          setPagination(null);
          return;
        }

        setChallenges(Array.isArray(data.challenges) ? data.challenges : []);
        setPagination(data.pagination || null);
      } catch (error) {
        if (!mounted) return;
        console.error("Unexpected error fetching private challenges:", error);
        setChallenges([]);
        setPagination(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    fetchChallenges();

    return () => {
      mounted = false;
    };
  }, [page]);

  const handleNavigate = (route) => history.push(route);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #f1f5f9",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2 },
        backgroundColor: "#ffffff",
        boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: { xs: 1, sm: 1.4 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "12px",
              backgroundColor: "#ede9fe",
              color: "#7c3aed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Swords size={20} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a", lineHeight: 1.2 }}>
              Mis retos
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", lineHeight: 1.2, mt: 0.15 }}>
              Retos que has creado o aceptado
            </Typography>
          </Box>
        </Box>
        <Button
          variant="text"
          endIcon={<ChevronRight size={15} />}
          onClick={() => history.push("/admin/retos")}
          sx={{ textTransform: "none", color: "#7c3aed", fontSize: "0.76rem", fontWeight: 600, minWidth: "auto", px: 0.5, flexShrink: 0 }}
        >
          Ver todos
        </Button>
      </Box>

      {isLoading ? (
        <Stack spacing={0.6}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Box
              key={`my-challenges-skeleton-${index}`}
              sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.1, py: 1.2 }}
            >
              <Skeleton variant="circular" width={40} height={40} animation="wave" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="55%" height={20} animation="wave" />
                <Skeleton variant="text" width="75%" height={16} animation="wave" />
              </Box>
              <Skeleton variant="rounded" width={88} height={28} animation="wave" />
            </Box>
          ))}
        </Stack>
      ) : challenges.length === 0 ? (
        <Box
          sx={{
            minHeight: 140,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.8,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "9999px",
              backgroundColor: "#f1f5f9",
              color: "#94a3b8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Users size={20} />
          </Box>
          <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 400 }}>
            Aún no has creado ni aceptado ningún reto.
          </Typography>
        </Box>
      ) : (
        <>
          <Stack spacing={0}>
            {challenges.map((challenge, index) => (
              <ChallengeRow
                key={challenge.challenge_id}
                challenge={challenge}
                onNavigate={handleNavigate}
                isLast={index === challenges.length - 1}
              />
            ))}
          </Stack>

          {pagination && pagination.total_pages > 1 ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 1.4 }}>
              <IconButton
                size="small"
                disabled={!pagination.has_previous_page}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                <ChevronLeft size={18} />
              </IconButton>
              <Typography sx={{ fontSize: "0.76rem", color: "#64748b" }}>
                {pagination.page} de {pagination.total_pages}
              </Typography>
              <IconButton
                size="small"
                disabled={!pagination.has_next_page}
                onClick={() => setPage((prev) => prev + 1)}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Box>
          ) : null}
        </>
      )}
    </Paper>
  );
}
